import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Button, Form, ProgressBar, Badge } from 'react-bootstrap';
import { PageTitle } from '../../../_metronic/layout/core';
import { KTCard, KTCardBody } from '../../../_metronic/helpers';
import { KTIcon } from '../../../_metronic/helpers';
import { License, LicenseSource, UploadProgress } from './types/assetTypes';
import { 
  uploadFileToWasabi, 
  uploadThumbnailToWasabi,
  generateFileId,
  clearUploadState,
  canResumeUpload,
} from './services/wasabiUploadService';
import { get_all_categories } from '../projects/api/projectCategoriesApi';
import { get_all_subcategories } from '../subcategories/api/subcategoriesApi';
import { get_all_tags, create_tag } from '../tags/api/tagsApi';
import { get_active_platforms, get_active_tags, get_active_projects, get_active_subprojects_of_project } from './api/downloadAssetsApi';
import { upload_asset_to_server, get_single_asset, update_upload_metadata } from './api/assetsApi';
import { permissionCheck } from '../../helper-functions/PermissionCheck';

interface CategoryItem {
  id: number
  category_name: string
}

interface SubcategoryItem {
  id: number
  subcategory_name: string
  category_id: number
}

interface ProjectItem {
  id: number
  project_name: string
}

interface SubprojectItem {
  id: number
  subproject_name?: string
  title?: string
  description?: string
  status?: string
  project_id: number
}

interface PlatformItem {
  id: number
  platform_name: string
  platform_url?: string
  status: number
}

interface TagItem {
  id: number
  tag_title: string
  status: number
}

interface ThumbnailItem {
  file: File
  isPrimary: boolean
  preview: string
}

interface ExtendedLicense extends License {
  project_id?: string | number
  subproject_id?: string | number
  isExisting?: boolean // Flag to mark existing licenses from API
  licenseId?: number // ID of existing license from API
  subprojectData?: any // Store subproject data for existing licenses
}

const UploadAssetPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  
  // Permission checks
  const canCreateAsset = Boolean(permissionCheck('upload-create'));
  const canUpdateAsset = Boolean(permissionCheck('upload-update'));
  
  // Redirect if no permission
  useEffect(() => {
    if (isEditMode && !canUpdateAsset) {
      toast.error('You do not have permission to update assets');
      navigate('/digital-assets');
    } else if (!isEditMode && !canCreateAsset) {
      toast.error('You do not have permission to create assets');
      navigate('/digital-assets');
    }
  }, [isEditMode, canCreateAsset, canUpdateAsset, navigate]);
  
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [thumbnails, setThumbnails] = useState<ThumbnailItem[]>([]);
  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);
  const [licenses, setLicenses] = useState<ExtendedLicense[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [resumableState, setResumableState] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryItem[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [subprojects, setSubprojects] = useState<SubprojectItem[]>([]);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [platforms, setPlatforms] = useState<PlatformItem[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isLoadingSubprojects, setIsLoadingSubprojects] = useState(false);
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(false);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [isSubcategoryDropdownOpen, setIsSubcategoryDropdownOpen] = useState(false);
  const [subcategorySearchQuery, setSubcategorySearchQuery] = useState('');
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [wasabiUploadComplete, setWasabiUploadComplete] = useState(false);
  const [existingUploadId, setExistingUploadId] = useState<string | null>(null);
  const [oldWasabiFilePath, setOldWasabiFilePath] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const licenseInputRef = useRef<HTMLInputElement>(null);

  // Load categories, subcategories, and projects on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingCategories(true)
        setIsLoadingProjects(true)
        setIsLoadingTags(true)
        setIsLoadingPlatforms(true)
        const [catsRes, subcatsRes, projectsRes, tagsRes, platformsRes] = await Promise.all([
          get_all_categories(),
          get_all_subcategories(),
          get_active_projects(),
          get_active_tags(),
          get_active_platforms()
        ])
        
        // Parse categories
        const apiCats = catsRes?.data?.all_categories || catsRes?.data?.categories || catsRes?.data || []
        const allCats: CategoryItem[] = (Array.isArray(apiCats) ? apiCats : []).map((c: any) => ({
          id: Number(c.id),
          category_name: c.category_name
        }))
        setCategories(allCats)
        
        // Parse subcategories
        const apiSubcats = subcatsRes?.data?.all_categories || subcatsRes?.data?.all_subcategories || subcatsRes?.data?.subcategories || subcatsRes?.data || []
        const allSubcats: SubcategoryItem[] = (Array.isArray(apiSubcats) ? apiSubcats : []).map((sc: any) => ({
          id: Number(sc.id),
          subcategory_name: sc.subcategory_name,
          category_id: Number(sc.category_id)
        }))
        setSubcategories(allSubcats)
        
        // Parse projects - API returns data.active_projects
        const apiProjects = projectsRes?.data?.active_projects || projectsRes?.data?.all_projects || projectsRes?.data?.projects || projectsRes?.data || []
        const allProjects: ProjectItem[] = (Array.isArray(apiProjects) ? apiProjects : []).map((p: any) => ({
          id: Number(p.id),
          project_name: p.project_name || p.name || ''
        }))
        setProjects(allProjects)
        
        // Don't parse subprojects from projects - they will be loaded when project is selected using active_subprojects_of_project
        setSubprojects([])
        
        // Parse tags - API returns data.active_tags
        const apiTags = tagsRes?.data?.active_tags || tagsRes?.data?.all_tags || tagsRes?.data?.tags || tagsRes?.data || []
        const allTags: TagItem[] = (Array.isArray(apiTags) ? apiTags : []).map((t: any) => ({
          id: Number(t.id),
          tag_title: t.tag_title || t.title || '',
          status: Number(t.status) || 1
        })).filter((t: TagItem) => t.status === 1)
        setTags(allTags)
        
        // Parse platforms
        const apiPlatforms = platformsRes?.data?.active_platforms || platformsRes?.data?.all_platforms || platformsRes?.data?.platforms || platformsRes?.data || []
        const allPlatforms: PlatformItem[] = (Array.isArray(apiPlatforms) ? apiPlatforms : []).map((p: any) => ({
          id: Number(p.id),
          platform_name: p.platform_name,
          platform_url: p.platform_url || '',
          status: Number(p.status) || 1
        })).filter((p: PlatformItem) => p.status === 1)
        setPlatforms(allPlatforms)
      } catch (error) {
        console.error('Error loading data:', error)
        toast.error('Failed to load data')
      } finally {
        setIsLoadingCategories(false)
        setIsLoadingProjects(false)
        setIsLoadingTags(false)
        setIsLoadingPlatforms(false)
      }
    }
    loadData()
  }, [])

  // Load asset data if in edit mode
  useEffect(() => {
    const loadAssetData = async () => {
      if (!isEditMode || !id) return;
      
      try {
        setIsLoadingAsset(true);
        const response = await get_single_asset(Number(id));
        const upload = response?.data?.upload_data || response?.data?.upload || response?.data;
        
        if (!upload) {
          toast.error('Asset not found');
          navigate('/digital-assets');
          return;
        }

        // Set existing upload ID
        if (upload.id) {
          setExistingUploadId(String(upload.id));
        }

        // Store old Wasabi file path for deletion when new file is uploaded
        if (upload.asset_file_path && upload.asset_file_path.trim() !== '') {
          setOldWasabiFilePath(upload.asset_file_path);
        }

        // Pre-fill form values
        const categoryId = upload.category_id ? String(upload.category_id) : (upload.category?.id ? String(upload.category.id) : '');
        const subcategoryIds = upload.subcategories && Array.isArray(upload.subcategories) && upload.subcategories.length > 0
          ? upload.subcategories.map((sc: any) => Number(sc.id)).filter((id: number) => !isNaN(id))
          : (upload.subcategory_id 
              ? (Array.isArray(upload.subcategory_id) 
                  ? upload.subcategory_id.map((id: any) => Number(id)).filter((id: number) => !isNaN(id))
                  : [Number(upload.subcategory_id)].filter((id: number) => !isNaN(id)))
              : []);
        
        formik.setValues({
          name: upload.upload_name || upload.name || '',
          category_id: categoryId,
          subcategory_id: subcategoryIds,
          description: upload.description || '',
          platform_id: upload.platform_id ? String(upload.platform_id) : (upload.platform?.id ? String(upload.platform.id) : ''),
          source_url: upload.source_url || '',
        });

        // Load subprojects from project_hierarchy or subprojects array (includes project data)
        let subprojectsData: SubprojectItem[] = [];
        
        // Check for project_hierarchy structure (new API response format)
        if (upload.project_hierarchy && Array.isArray(upload.project_hierarchy)) {
          upload.project_hierarchy.forEach((project: any) => {
            if (project.subprojects && Array.isArray(project.subprojects)) {
              const projectSubprojects = project.subprojects.map((sp: any) => ({
                id: Number(sp.id),
                subproject_name: sp.subproject_name || sp.title || sp.name || '',
                title: sp.title || sp.name || sp.subproject_name || '',
                description: sp.description || '',
                status: sp.status || 'active',
                project_id: Number(project.id || project.project_id || 0)
              }));
              subprojectsData = [...subprojectsData, ...projectSubprojects];
            }
          });
        }
        // Fallback to direct subprojects array (old API response format)
        else if (upload.subprojects && Array.isArray(upload.subprojects)) {
          subprojectsData = upload.subprojects.map((sp: any) => ({
            id: Number(sp.id),
            subproject_name: sp.subproject_name || sp.title || sp.name || '',
            title: sp.title || sp.name || sp.subproject_name || '',
            description: sp.description || '',
            status: sp.status || 'active',
            project_id: Number(sp.project_id || (sp.project?.id || 0))
          }));
        }
        
        if (subprojectsData.length > 0) {
          setSubprojects(subprojectsData);
        }

        // Set tags
        if (upload.tags && Array.isArray(upload.tags)) {
          const tagIds: number[] = [];
          upload.tags.forEach((tag: any) => {
            const tagId = tag.id || tag.tag_id;
            if (tagId) {
              tagIds.push(Number(tagId));
            }
          });
          setSelectedTagIds(tagIds);
        }

        // Set licenses (if available in response) - mark as existing
        // Check licenses from project_hierarchy structure first
        let licensesData: ExtendedLicense[] = [];
        
        if (upload.project_hierarchy && Array.isArray(upload.project_hierarchy)) {
          upload.project_hierarchy.forEach((project: any) => {
            if (project.subprojects && Array.isArray(project.subprojects)) {
              project.subprojects.forEach((subproject: any) => {
                if (subproject.licenses && Array.isArray(subproject.licenses)) {
                  subproject.licenses.forEach((lic: any) => {
                    licensesData.push({
                      source: lic.source || LicenseSource.CUSTOM,
                      license_url: lic.license_file_path || lic.license_url || '',
                      project_id: String(project.id || project.project_id || 0),
                      subproject_id: String(subproject.id || subproject.subproject_id || 0),
                      purchased_date: lic.license_date || lic.purchased_date || '',
                      license_text: lic.description || lic.license_text || '',
                      isExisting: true, // Mark as existing license
                      licenseId: lic.id, // Store license ID
                      subprojectData: {
                        id: Number(subproject.id || subproject.subproject_id || 0),
                        subproject_name: subproject.subproject_name || subproject.title || subproject.name || '',
                        title: subproject.subproject_name || subproject.title || subproject.name || '',
                        description: subproject.description || '',
                        status: subproject.status || 'active',
                        project_id: Number(project.id || project.project_id || 0)
                      }
                    });
                  });
                }
              });
            }
          });
        }
        // Fallback to direct licenses array (old API response format)
        else if (upload.licenses && Array.isArray(upload.licenses)) {
          licensesData = upload.licenses.map((lic: any) => {
            // Get subproject data from licenses array or subprojects array
            const subprojectData = lic.subproject || upload.subprojects?.find((sp: any) => sp.id === lic.subproject_id);
            
            return {
              source: lic.source || LicenseSource.CUSTOM,
              license_url: lic.license_file_path || lic.license_url || '',
              project_id: subprojectData?.project_id ? String(subprojectData.project_id) : (subprojectData?.project?.id ? String(subprojectData.project.id) : undefined),
              subproject_id: lic.subproject_id ? String(lic.subproject_id) : undefined,
              purchased_date: lic.license_date || lic.purchased_date || '',
              license_text: lic.description || lic.license_text || '',
              isExisting: true, // Mark as existing license
              licenseId: lic.id, // Store license ID
              subprojectData: subprojectData // Store subproject data with project info
            };
          });
        }
        
        if (licensesData.length > 0) {
          setLicenses(licensesData);
        }

        // Load thumbnails (if available) from thumbnails array
        if (upload.thumbnails && Array.isArray(upload.thumbnails) && upload.thumbnails.length > 0) {
          // Helper function to construct full URL from file path (same as in listing)
          const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
            if (!filePath) return null;
            
            // If already a full URL, return as is
            if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
              return filePath;
            }
            
            // Construct full URL from base URL
            const apiBaseUrl = process.env.REACT_APP_API_URL || '';
            // Remove /api suffix if present, but keep the base domain
            let cleanBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
            // Ensure base URL doesn't end with slash
            cleanBaseUrl = cleanBaseUrl.replace(/\/$/, '');
            
            // Handle file path - remove leading slash if present, then add it back
            const cleanPath = filePath.replace(/^\//, '');
            
            return `${cleanBaseUrl}/${cleanPath}`;
          };

          const thumbnailsData: ThumbnailItem[] = upload.thumbnails.map((thumb: any, idx: number) => {
            const filePath = thumb.file_path || thumb.image_url || thumb.url || thumb.thumbnail_url || '';
            const fullUrl = getThumbnailUrl(filePath);
            
            return {
              file: null as any, // We don't have the file, just the URL
              isPrimary: thumb.is_primary === 1 || thumb.is_primary === true || thumb.is_primary === '1' || idx === 0,
              preview: fullUrl || ''
            };
          });
          setThumbnails(thumbnailsData);
        }

        toast.success('Asset data loaded successfully');
      } catch (error: any) {
        console.error('Error loading asset:', error);
        toast.error(error?.response?.data?.message || 'Failed to load asset data');
        navigate('/digital-assets');
      } finally {
        setIsLoadingAsset(false);
      }
    };

    if (isEditMode && id && categories.length > 0 && tags.length > 0 && platforms.length > 0) {
      loadAssetData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, categories.length, subcategories.length, tags.length, platforms.length])

  // Warn user before leaving during upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = 'Upload is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-subcategory-dropdown]')) {
        setIsSubcategoryDropdownOpen(false);
      }
      if (!target.closest('[data-tags-dropdown]')) {
        setIsTagsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Asset name is required'),
    category_id: Yup.string().required('Category is required'),
    subcategory_id: Yup.array().of(Yup.number()).min(1, 'At least one subcategory is required'),
    description: Yup.string(),
    platform_id: Yup.string().required('Platform is required'),
    source_url: Yup.string().url('Invalid URL').required('Source URL is required'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      category_id: '',
      subcategory_id: [] as number[],
      description: '',
      platform_id: '',
      source_url: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!fileInputRef.current?.files || fileInputRef.current.files.length === 0) {
        toast.error('Please select a file to upload');
        return;
      }

      // Validate thumbnails
      if (thumbnails.length === 0) {
        toast.error('Please add at least one thumbnail');
        return;
      }

      // Validate tags
      if (selectedTagIds.length === 0 && newTags.length === 0) {
        toast.error('Please select or add at least one tag');
        return;
      }

      // Validate licenses
      if (licenses.length === 0) {
        toast.error('Please add at least one license');
        return;
      }

      // Validate each license has required fields
      for (let i = 0; i < licenses.length; i++) {
        const license = licenses[i];
        // Skip validation for existing licenses in edit mode
        if (isEditMode && license.isExisting) {
          continue;
        }
        
        if (!license.license_file || !(license.license_file instanceof File)) {
          toast.error(`License ${i + 1}: License File is required`);
          return;
        }
        
        if (!license.purchased_date || license.purchased_date.trim() === '') {
          toast.error(`License ${i + 1}: Purchased Date is required`);
          return;
        }
      }

      const file = fileInputRef.current.files[0];
      await handleFileUpload(file, values);
    },
  });

  // Get filtered subcategories based on selected category
  const filteredSubcategories = useMemo(() => {
    return formik.values.category_id 
      ? subcategories.filter(sc => sc.category_id === Number(formik.values.category_id))
      : []
  }, [formik.values.category_id, subcategories])

  const handleFileUpload = async (file: File, formValues: any) => {
    // Validate file type - must be zip
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    const validExtensions = ['.zip'];
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Only ZIP files are allowed. Please select a .zip file.');
      setIsUploading(false);
      return;
    }
    
    setIsUploading(true);
    const fileId = generateFileId(file.name, file.size);
    
    // Check if resuming
    const isResuming = canResumeUpload(file.name, file.size) !== null;

    const progress: UploadProgress = {
      file_name: file.name,
      uploaded_bytes: 0,
      total_bytes: file.size,
      percentage: 0,
      status: isResuming ? 'resuming' : 'uploading'
    };
    setUploadProgress(progress);
    
    if (isResuming) {
      toast.info('Resuming upload from where it left off...');
    }

    try {
      // Step 1: Prepare FormData for uploads API (without main file)
      const uploadFormData = new FormData();
      
      // Basic fields
      uploadFormData.append('upload_name', formValues.name);
      uploadFormData.append('category_id', formValues.category_id);
      
      // Subcategories - send as array
      const subcategoryIds = Array.isArray(formValues.subcategory_id) ? formValues.subcategory_id : (formValues.subcategory_id ? [formValues.subcategory_id] : []);
      subcategoryIds.forEach(subcatId => {
        uploadFormData.append('subcategory_id[]', subcatId.toString());
      });
      
      uploadFormData.append('description', formValues.description || '');
      uploadFormData.append('status', '1'); // Active
      uploadFormData.append('source_url', formValues.source_url || '');
      
      // Platform ID
      if (formValues.platform_id) {
        uploadFormData.append('platform_id', formValues.platform_id);
      }
      
      // Tags - existing tags as tags_title[]
      selectedTagIds.forEach(tagId => {
        const tag = tags.find(t => t.id === tagId);
        if (tag) {
          uploadFormData.append('tags_title[]', tag.tag_title);
        }
      });
      // New tags also as tags_title[]
      newTags.forEach(tagTitle => {
        uploadFormData.append('tags_title[]', tagTitle);
      });
      
      // Thumbnails with primary/secondary - only send new thumbnails (with file), skip existing ones (only preview URL)
      let thumbnailIndex = 0;
      thumbnails.forEach((thumbnail) => {
        // Only append thumbnails that have actual file (new uploads), skip existing ones that only have preview URL
        if (thumbnail.file) {
          uploadFormData.append(`thumbnails[${thumbnailIndex}][image_file]`, thumbnail.file);
          uploadFormData.append(`thumbnails[${thumbnailIndex}][is_primary]`, thumbnail.isPrimary ? '1' : '0');
          thumbnailIndex++;
        }
      });
      
      // Subprojects with licenses - only include new licenses (not existing ones) in edit mode
      const licensesBySubproject = new Map<number, ExtendedLicense[]>();
      const projectIds = new Set<string | number>();
      licenses.forEach(license => {
        // In edit mode, skip existing licenses (they're already in the database)
        if (isEditMode && license.isExisting) {
          return;
        }
        
        if (license.subproject_id && license.project_id) {
          const subprojectId = Number(license.subproject_id);
          if (!licensesBySubproject.has(subprojectId)) {
            licensesBySubproject.set(subprojectId, []);
          }
          licensesBySubproject.get(subprojectId)!.push(license);
          projectIds.add(license.project_id);
        }
      });
      
      // Add project_id (use first project_id if multiple licenses have same project, or first one if different)
      if (projectIds.size > 0) {
        const firstProjectId = Array.from(projectIds)[0];
        uploadFormData.append('project_id', String(firstProjectId));
      }
      
      // Add subprojects with licenses
      let subprojectIndex = 0;
      licensesBySubproject.forEach((subprojectLicenses, subprojectId) => {
        uploadFormData.append(`subprojects[${subprojectIndex}][id]`, subprojectId.toString());
        
        // Add project_id for this subproject (from first license in group)
        if (subprojectLicenses.length > 0 && subprojectLicenses[0].project_id) {
          uploadFormData.append(`subprojects[${subprojectIndex}][project_id]`, String(subprojectLicenses[0].project_id));
        }
        
        // Add licenses for this subproject
        subprojectLicenses.forEach((license, licenseIndex) => {
          if (license.license_file && license.license_file instanceof File) {
            uploadFormData.append(`subprojects[${subprojectIndex}][licenses][${licenseIndex}][file]`, license.license_file);
          }
          // Format date as Y-m-d
          if (license.purchased_date) {
            const date = new Date(license.purchased_date);
            const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            uploadFormData.append(`subprojects[${subprojectIndex}][licenses][${licenseIndex}][license_date]`, formattedDate);
          }
          if (license.license_text) {
            uploadFormData.append(`subprojects[${subprojectIndex}][licenses][${licenseIndex}][license_text]`, license.license_text);
          }
        });
        
        subprojectIndex++;
      });
      
      // Step 2: Call uploads API to create/update metadata (before Wasabi upload)
      let responseUploadId: string | null = null;
      
      setUploadProgress({
        ...progress,
        percentage: 10,
        status: 'processing'
      });
      
      try {
        if (isEditMode && existingUploadId) {
          // In edit mode, update existing upload record
          responseUploadId = existingUploadId;
          setUploadId(responseUploadId);
          
          // Debug: Log FormData contents
          console.log('Update API - Upload ID:', existingUploadId);
          console.log('Update API - FormData keys:', Array.from(uploadFormData.keys()));
          
          const updateResponse = await update_upload_metadata(existingUploadId, uploadFormData, (apiProgress) => {
            const totalProgress = 10 + (apiProgress * 0.1);
            setUploadProgress({
              ...progress,
              percentage: totalProgress,
              status: 'processing'
            });
          });
          
          console.log('Update API - Response:', updateResponse);
          toast.success('Metadata updated successfully. Starting file upload...');
        } else {
          // Create new upload record
          const uploadResponse = await upload_asset_to_server(uploadFormData, (apiProgress) => {
            const totalProgress = 10 + (apiProgress * 0.1);
            setUploadProgress({
              ...progress,
              percentage: totalProgress,
              status: 'processing'
            });
          });
          
          // Get upload_id from response
          responseUploadId = uploadResponse?.data?.upload?.id || uploadResponse?.data?.id || uploadResponse?.upload_id;
          if (!responseUploadId) {
            throw new Error('Failed to get upload_id from API response');
          }
          
          setUploadId(String(responseUploadId));
          toast.success('Metadata saved successfully. Starting file upload...');
        }
      } catch (apiError: any) {
        console.error('API upload error:', apiError);
        console.error('Error details:', {
          message: apiError?.message,
          response: apiError?.response?.data,
          status: apiError?.response?.status,
          url: apiError?.config?.url,
          method: apiError?.config?.method,
        });
        const errorMessage = apiError?.response?.data?.message || 
                             apiError?.response?.data?.error || 
                             apiError?.message || 
                             'Failed to save metadata. Upload cancelled.';
        toast.error(errorMessage);
        setIsUploading(false);
        throw apiError; // Stop the process if API call fails
      }
      
      // Step 3: Delete old Wasabi file if in edit mode and old file exists
      if (isEditMode && oldWasabiFilePath) {
        setUploadProgress({
          ...progress,
          percentage: 15,
          status: 'processing'
        });
        
        try {
          const { deleteFileFromWasabi } = await import('./services/wasabiUploadService');
          const deleteResult = await deleteFileFromWasabi(oldWasabiFilePath);
          if (deleteResult.success) {
            if (deleteResult.deleted) {
              console.log('✅ Old Wasabi file deleted successfully:', deleteResult.message);
              toast.success('Old file deleted from Wasabi. Uploading new file...');
            } else {
              console.log('ℹ️ Old file already deleted or not found:', deleteResult.message);
              toast.info('Old file not found in Wasabi (may have been already deleted). Uploading new file...');
            }
          } else {
            console.error('❌ Failed to delete old Wasabi file:', deleteResult.message);
            toast.warning('Could not delete old file from Wasabi, but proceeding with new upload...');
          }
        } catch (deleteError: any) {
          console.error('❌ Failed to delete old Wasabi file:', deleteError);
          // Continue with upload even if deletion fails (non-critical)
          toast.warning('Could not delete old file from Wasabi, but proceeding with new upload...');
        }
      }

      // Step 4: Upload file to Wasabi (only after successful API call)
      setUploadProgress({
        ...progress,
        percentage: 20,
        status: 'uploading'
      });
      
      const { fileUrl, key } = await uploadFileToWasabi(
        file,
        (progressData) => {
          // Map Wasabi progress (20% to 90%)
          const wasabiProgress = 20 + (progressData.percentage * 0.7);
          setUploadProgress({
            ...progress,
            uploaded_bytes: progressData.uploaded,
            percentage: wasabiProgress,
            status: 'uploading'
          });
        },
        fileId
      );

      // Mark Wasabi upload as complete
      setWasabiUploadComplete(true);
      
      // Clear old file path after successful upload
      if (oldWasabiFilePath) {
        setOldWasabiFilePath(null);
      }
      
      // Step 5: Call upload_response API when Wasabi upload completes
      setUploadProgress({
        ...progress,
        percentage: 95,
        status: 'processing'
      });
      
      try {
        const { upload_response } = await import('./api/assetsApi');
        const assetFilePath = fileUrl; // Use full Wasabi URL (Location) as file path
        
        await upload_response(String(responseUploadId), assetFilePath);
        
        toast.success('File uploaded and associated successfully!');
      } catch (responseError: any) {
        console.error('Upload response API error:', responseError);
        toast.warning('File uploaded to Wasabi, but association failed. Please contact support.');
      }
      
      // Save metadata to localStorage (for backward compatibility)
      const { saveAssetMetadata } = await import('./services/wasabiUploadService');
      const thumbnailUrls = thumbnails.map(t => t.preview); // Use preview URLs for localStorage
      saveAssetMetadata(key, {
        name: formValues.name,
        category_id: formValues.category_id,
        subcategory_id: subcategoryIds,
        description: formValues.description,
        tags: [...selectedTagIds.map(id => tags.find(t => t.id === id)?.tag_title || ''), ...newTags].filter(Boolean),
        thumbnails: thumbnailUrls,
        licenses: licenses,
        platform_id: formValues.platform_id,
        source_url: formValues.source_url,
        uploaded_at: new Date().toISOString(),
      });

      setUploadProgress({
        ...progress,
        percentage: 100,
        status: 'completed'
      });

      toast.success(isEditMode ? 'Asset file uploaded and updated successfully!' : 'Asset uploaded successfully!');
      
      // Clear upload state
      clearUploadState(fileId);
      
      handleReset();
      // Navigate back to digital assets list after successful upload
      setTimeout(() => {
        navigate('/digital-assets');
      }, 1500);
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Upload failed. You can resume the upload later.';
      toast.error(errorMessage);
      setUploadProgress({
        ...progress,
        status: 'failed',
        error: errorMessage
      });
      // Don't clear state on error - allows resuming
    } finally {
      setIsUploading(false);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // Validate that all selected files are images
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
      const validImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
      
      const invalidFiles: string[] = [];
      const validFiles: File[] = [];
      
      Array.from(e.target.files).forEach((file) => {
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
        
        // Check MIME type
        const isValidMimeType = file.type && validImageTypes.includes(file.type);
        // Check file extension
        const isValidExtension = validImageExtensions.includes(fileExtension);
        
        if (!isValidMimeType && !isValidExtension) {
          invalidFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });
      
      // Show error if any invalid files found
      if (invalidFiles.length > 0) {
        toast.error(`Only image files are allowed. Invalid files: ${invalidFiles.join(', ')}`);
        // Clear the input
        if (thumbnailInputRef.current) {
          thumbnailInputRef.current.value = '';
        }
        // Only proceed if there are valid files
        if (validFiles.length === 0) {
          return;
        }
      }
      
      // Process only valid files
      const hasExistingPrimary = thumbnails.some(t => t.isPrimary);
      const newThumbnails: ThumbnailItem[] = validFiles.map((file, idx) => ({
        file,
        // Only first new thumbnail is primary if no existing primary exists
        isPrimary: !hasExistingPrimary && idx === 0,
        preview: URL.createObjectURL(file)
      }));
      setThumbnails([...thumbnails, ...newThumbnails]);
    }
  };

  const removeThumbnail = (index: number) => {
    const removed = thumbnails[index];
    URL.revokeObjectURL(removed.preview);
    const newThumbnails = thumbnails.filter((_, i) => i !== index);
    // If primary was removed, make first one primary
    if (removed.isPrimary && newThumbnails.length > 0) {
      newThumbnails[0].isPrimary = true;
    }
    setThumbnails(newThumbnails);
  };

  const setPrimaryThumbnail = (index: number) => {
    // Ensure only one thumbnail is primary
    const newThumbnails = thumbnails.map((thumb, i) => ({
      ...thumb,
      isPrimary: i === index // Only the selected index is primary, all others are false
    }));
    setThumbnails(newThumbnails);
  };

  const handleLicenseAdd = () => {
    setLicenses([...licenses, {
      source: LicenseSource.OTHER,
      project_id: '',
      license_file: undefined,
      license_url: '',
      license_text: '',
    }]);
  };

  const updateLicense = (index: number, field: keyof ExtendedLicense, value: any) => {
    const updated = [...licenses];
    updated[index] = { ...updated[index], [field]: value };
    setLicenses(updated);
  };

  const removeLicense = (index: number) => {
    const license = licenses[index];
    // Don't allow removing existing licenses
    if (license.isExisting) {
      toast.warning('Cannot remove existing licenses. They are read-only.');
      return;
    }
    setLicenses(licenses.filter((_, i) => i !== index));
  };

  const handleLicenseFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      updateLicense(index, 'license_file', e.target.files[0]);
    }
  };

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleRemoveTag = (tagId: number) => {
    setSelectedTagIds(prev => prev.filter(id => id !== tagId));
  };

  // Load subprojects for a specific project
  const loadSubprojectsForProject = async (projectId: string | number) => {
    if (!projectId) {
      setSubprojects([]);
      return;
    }

    try {
      setIsLoadingSubprojects(true);
      const response = await get_active_subprojects_of_project(String(projectId));
      const apiSubprojects = response?.data?.active_subprojects_of_project || response?.data?.active_subprojects || response?.data?.subprojects || response?.data?.all_subprojects || response?.data || [];
      const allSubprojects: SubprojectItem[] = (Array.isArray(apiSubprojects) ? apiSubprojects : []).map((sp: any) => ({
        id: Number(sp.id),
        subproject_name: sp.subproject_name || sp.title || '',
        title: sp.subproject_name || sp.title || '',
        description: sp.description || '',
        status: sp.status || 'active',
        project_id: Number(projectId)
      }));
      setSubprojects(allSubprojects);
    } catch (error) {
      console.error('Error loading subprojects:', error);
      toast.error('Failed to load subprojects');
      setSubprojects([]);
    } finally {
      setIsLoadingSubprojects(false);
    }
  };

  const handleAddNewTag = async () => {
    // Use tagSearchQuery instead of tagInput since user types in search field
    const trimmedInput = tagSearchQuery.trim();
    if (!trimmedInput) {
      toast.error('Please enter a tag name');
      return;
    }

    // Check if tag already exists in selected tags
    const existingTag = tags.find(t => t.tag_title.toLowerCase() === trimmedInput.toLowerCase());
    if (existingTag) {
      // If exists, just select it
      if (!selectedTagIds.includes(existingTag.id)) {
        setSelectedTagIds(prev => [...prev, existingTag.id]);
        toast.success('Tag selected');
      } else {
        toast.info('Tag already selected');
      }
      setTagSearchQuery('');
      return;
    }

    // Check if already in new tags
    if (newTags.includes(trimmedInput)) {
      toast.info('Tag already added');
      setTagSearchQuery('');
      return;
    }

    try {
      // Create new tag via API
      const response = await create_tag({
        tag_title: trimmedInput,
        description: '',
        status: '1'
      });
      
      if (response.status) {
        const newTagId = response.data?.tag?.id || response.data?.id;
        if (newTagId) {
          setSelectedTagIds(prev => [...prev, Number(newTagId)]);
          // Reload tags to include new one
          const tagsRes = await get_active_tags();
          const apiTags = tagsRes?.data?.active_tags || tagsRes?.data?.all_tags || tagsRes?.data?.tags || tagsRes?.data || [];
          const allTags: TagItem[] = (Array.isArray(apiTags) ? apiTags : []).map((t: any) => ({
            id: Number(t.id),
            tag_title: t.tag_title || t.title || '',
            status: Number(t.status) || 1
          })).filter((t: TagItem) => t.status === 1);
          setTags(allTags);
        }
        setNewTags(prev => [...prev, trimmedInput]);
        setTagSearchQuery('');
        setIsTagsDropdownOpen(false);
        toast.success('Tag added successfully');
      } else {
        toast.error('Failed to create tag');
      }
    } catch (error: any) {
      console.error('Error creating tag:', error);
      toast.error(error?.response?.data?.message || 'Failed to create tag');
    }
  };

  const filteredTagsForDropdown = useMemo(() => {
    if (!tagSearchQuery) return tags;
    return tags.filter(tag =>
      tag.tag_title.toLowerCase().includes(tagSearchQuery.toLowerCase())
    );
  }, [tags, tagSearchQuery]);

  const handleReset = () => {
    formik.resetForm();
    setThumbnails([]);
    setLicenses([]);
    setTags([]);
    setTagInput('');
    setUploadProgress(null);
    setIsUploading(false);
    setResumableState(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
    if (licenseInputRef.current) licenseInputRef.current.value = '';
  };

  // Reset subcategory when category changes
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.setFieldValue('category_id', e.target.value)
    formik.setFieldValue('subcategory_id', []) // Reset subcategory when category changes
  }

  const handleSubcategoryToggle = (subcategoryId: number) => {
    const currentIds = formik.values.subcategory_id || []
    const newIds = currentIds.includes(subcategoryId)
      ? currentIds.filter(id => id !== subcategoryId)
      : [...currentIds, subcategoryId]
    formik.setFieldValue('subcategory_id', newIds)
  }

  const handleRemoveSubcategory = (subcategoryId: number) => {
    const currentIds = formik.values.subcategory_id || []
    formik.setFieldValue('subcategory_id', currentIds.filter(id => id !== subcategoryId))
  }

  const handleClearAllSubcategories = () => {
    formik.setFieldValue('subcategory_id', [])
  }

  const filteredSubcategoriesForDropdown = useMemo(() => {
    if (!subcategorySearchQuery) return filteredSubcategories
    return filteredSubcategories.filter(subcat =>
      subcat.subcategory_name.toLowerCase().includes(subcategorySearchQuery.toLowerCase())
    )
  }, [filteredSubcategories, subcategorySearchQuery])

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>{isEditMode ? 'Edit & Upload Asset' : 'Upload Digital Asset'}</PageTitle>
      <style>{`
        /* Fix select dropdown options visibility in dark mode */
        [data-bs-theme="dark"] select option,
        [data-bs-theme="dark"] .form-select option {
          background-color: var(--bs-body-bg) !important;
          color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] select option:checked,
        [data-bs-theme="dark"] .form-select option:checked {
          background-color: var(--bs-primary) !important;
          color: var(--bs-white) !important;
        }
        
        /* Fix select dropdown when open - for native select */
        [data-bs-theme="dark"] select:focus option,
        [data-bs-theme="dark"] .form-select:focus option {
          background-color: var(--bs-body-bg) !important;
          color: var(--bs-body-color) !important;
        }
        
        /* Fix subcategory dropdown items in dark mode */
        [data-bs-theme="dark"] [data-subcategory-dropdown] .p-2 {
          color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .p-2:hover {
          background-color: var(--bs-gray-800) !important;
          color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .bg-light-primary {
          background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
          color: var(--bs-primary) !important;
        }
        
        /* Fix dropdown container background in dark mode */
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-dropdown-container {
          background-color: var(--bs-body-bg) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-dropdown-container .p-3 {
          background-color: var(--bs-body-bg) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-dropdown-container .subcategory-list-container {
          background-color: var(--bs-body-bg) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-dropdown-container .form-control {
          background-color: var(--bs-body-bg) !important;
          color: var(--bs-body-color) !important;
        }
        
        /* Ensure text color is always visible on hover for subcategory items */
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item {
          color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item .subcategory-text {
          color: var(--bs-body-color) !important;
          -webkit-text-fill-color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item:hover,
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.subcategory-hover {
          background-color: #2b2b40 !important;
          color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item:hover .subcategory-text,
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.subcategory-hover .subcategory-text {
          color: var(--bs-body-color) !important;
          -webkit-text-fill-color: var(--bs-body-color) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.bg-light-primary {
          background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
          color: var(--bs-primary) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.bg-light-primary .subcategory-text {
          color: var(--bs-primary) !important;
          -webkit-text-fill-color: var(--bs-primary) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.bg-light-primary:hover,
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.bg-light-primary.subcategory-hover {
          background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
          color: var(--bs-primary) !important;
        }
        
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.bg-light-primary:hover .subcategory-text,
        [data-bs-theme="dark"] [data-subcategory-dropdown] .subcategory-item.bg-light-primary.subcategory-hover .subcategory-text {
          color: var(--bs-primary) !important;
          -webkit-text-fill-color: var(--bs-primary) !important;
        }
      `}</style>
      <div className='d-flex flex-column flex-lg-row'>
        <div className='flex-lg-row-fluid'>
          <KTCard>
            <KTCardBody>
              {isLoadingAsset ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="text-muted">Loading asset data...</div>
                </div>
              ) : (
              <Form onSubmit={formik.handleSubmit}>
                <div className="mb-5">
                  <label className="form-label required">Asset Name</label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.name && formik.touched.name}
                  />
                  {formik.errors.name && formik.touched.name && (
                    <div className="text-danger">{formik.errors.name}</div>
                  )}
                </div>

                <div className="mb-5">
                  <label className="form-label required">Category</label>
                  <Form.Select
                    name="category_id"
                    value={formik.values.category_id}
                    onChange={handleCategoryChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.category_id && formik.touched.category_id}
                    disabled={isLoadingCategories}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                    ))}
                  </Form.Select>
                  {formik.errors.category_id && formik.touched.category_id && (
                    <div className="text-danger">{formik.errors.category_id}</div>
                  )}
                  {isLoadingCategories && <div className="form-text">Loading categories...</div>}
                </div>

                <div className="mb-5">
                  <label className="form-label required">Subcategory</label>
                  {!formik.values.category_id ? (
                    <div className="form-text text-muted">Please select a category first</div>
                  ) : filteredSubcategories.length === 0 ? (
                    <div className="form-text text-muted">No subcategories available for this category</div>
                  ) : (
                    <div className="position-relative" data-subcategory-dropdown>
                      {/* Multiselect Input */}
                      <div 
                        className="form-control form-control-solid d-flex align-items-center flex-wrap gap-2"
                        style={{ minHeight: '42px', cursor: 'pointer' }}
                        onClick={() => setIsSubcategoryDropdownOpen(!isSubcategoryDropdownOpen)}
                      >
                        {(formik.values.subcategory_id || []).length === 0 ? (
                          <span className="text-muted">Select subcategories...</span>
                        ) : (
                          (formik.values.subcategory_id || []).map(subcatId => {
                            // First try to find in filteredSubcategories, then in all subcategories
                            const subcat = filteredSubcategories.find(sc => sc.id === subcatId) || 
                                          subcategories.find(sc => sc.id === subcatId)
                            // If still not found, it might be loading - show the ID temporarily
                            if (!subcat) {
                              console.warn(`Subcategory with ID ${subcatId} not found in available subcategories`)
                              return null
                            }
                            return (
                              <span 
                                key={subcatId}
                                className="badge badge-light-primary d-inline-flex align-items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemoveSubcategory(subcatId)
                                }}
                              >
                                {subcat.subcategory_name}
                                <KTIcon 
                                  iconName="cross" 
                                  className="fs-6 cursor-pointer"
                                />
                              </span>
                            )
                          }).filter(Boolean)
                        )}
                        <div className="ms-auto d-flex align-items-center gap-2">
                          {(formik.values.subcategory_id || []).length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-icon btn-light"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClearAllSubcategories()
                              }}
                              title="Clear all subcategories"
                            >
                              <KTIcon iconName="cross" className="fs-5" />
                            </button>
                          )}
                          <KTIcon 
                            iconName={isSubcategoryDropdownOpen ? 'up' : 'down'} 
                            className="fs-5 text-muted"
                          />
                        </div>
                      </div>

                      {/* Dropdown Menu */}
                      {isSubcategoryDropdownOpen && (
                        <div
                          className="position-absolute w-100 border border-gray-300 rounded shadow-lg subcategory-dropdown-container"
                          style={{ 
                            top: '100%', 
                            zIndex: 1000, 
                            maxHeight: '300px', 
                            overflowY: 'auto',
                            marginTop: '4px',
                            backgroundColor: 'var(--bs-body-bg)',
                            borderColor: 'var(--bs-border-color)'
                          }}
                        >
                          {/* Search Input */}
                          <div className="p-3 border-bottom" style={{ borderColor: 'var(--bs-border-color)' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search subcategories..."
                              value={subcategorySearchQuery}
                              onChange={(e) => setSubcategorySearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Subcategories List */}
                          <div className="p-2 subcategory-list-container" style={{ backgroundColor: 'var(--bs-body-bg)' }}>
                            {filteredSubcategoriesForDropdown.length === 0 ? (
                              <div className="text-center py-3 fs-7" style={{ color: 'var(--bs-body-color)' }}>
                                {subcategorySearchQuery ? 'No subcategories found' : 'No subcategories available'}
                              </div>
                            ) : (
                              filteredSubcategoriesForDropdown.map(subcat => {
                                const isSelected = (formik.values.subcategory_id || []).includes(subcat.id)
                                return (
                                  <div
                                    key={subcat.id}
                                    className={`p-2 rounded cursor-pointer subcategory-item ${isSelected ? 'bg-light-primary' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleSubcategoryToggle(subcat.id)
                                    }}
                                    style={{ 
                                      cursor: 'pointer',
                                      color: isSelected ? 'var(--bs-primary)' : 'var(--bs-body-color)',
                                      backgroundColor: isSelected ? 'rgba(var(--bs-primary-rgb), 0.1)' : 'transparent',
                                      transition: 'background-color 0.2s ease, color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
                                      e.currentTarget.classList.add('subcategory-hover');
                                      if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = isDark ? '#2b2b40' : 'var(--bs-gray-100)';
                                      } else {
                                        e.currentTarget.style.backgroundColor = 'rgba(var(--bs-primary-rgb), 0.15)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.classList.remove('subcategory-hover');
                                      if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      } else {
                                        e.currentTarget.style.backgroundColor = 'rgba(var(--bs-primary-rgb), 0.1)';
                                      }
                                    }}
                                  >
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span 
                                        className={`subcategory-text ${isSelected ? 'fw-bold' : ''}`}
                                        style={{ 
                                          color: isSelected ? 'var(--bs-primary)' : 'var(--bs-body-color)',
                                          transition: 'color 0.2s ease'
                                        }}
                                      >
                                        {subcat.subcategory_name}
                                      </span>
                                      {isSelected && (
                                        <KTIcon iconName="check" className="fs-5 text-primary" />
                                      )}
                                    </div>
                                  </div>
                                )
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {formik.errors.subcategory_id && formik.touched.subcategory_id && (
                    <div className="text-danger mt-2">{formik.errors.subcategory_id}</div>
                  )}
                </div>

                <div className="mb-5">
                  <label className="form-label">Description</label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </div>

                <div className="mb-5">
                  <label className="form-label required">Zip File</label>
                  {isEditMode && oldWasabiFilePath && (
                    <div className="alert alert-warning mb-3">
                      <KTIcon iconName="information" className="me-2" />
                      <strong>Note:</strong> Selecting a new file will replace the existing file on Wasabi. The old file will be automatically deleted.
                    </div>
                  )}
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      const file = target.files?.[0];
                      if (file) {
                        // Validate file type - must be zip
                        const fileName = file.name.toLowerCase();
                        const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
                        const validExtensions = ['.zip'];
                        const validMimeTypes = ['application/zip', 'application/x-zip-compressed', 'application/x-zip'];
                        
                        // Check file extension
                        if (!validExtensions.includes(fileExtension)) {
                          toast.error('Only ZIP files are allowed. Please select a .zip file.');
                          target.value = ''; // Clear the input
                          setSelectedFile(null);
                          setResumableState(null);
                          return;
                        }
                        
                        // Check MIME type if available
                        if (file.type && !validMimeTypes.includes(file.type)) {
                          // Some browsers may not set MIME type correctly, so we'll allow if extension is correct
                          // But if MIME type is set and it's wrong, show warning
                          if (file.type !== '') {
                            console.warn('File MIME type mismatch, but extension is correct:', file.type);
                          }
                        }
                        
                        setSelectedFile(file);
                        // Check if this file can be resumed
                        const resumeState = canResumeUpload(file.name, file.size);
                        setResumableState(resumeState);
                        if (resumeState) {
                          const uploadedChunks = resumeState.uploadedChunks.size;
                          const totalChunks = Math.ceil(file.size / (10 * 1024 * 1024));
                          const progress = Math.round((uploadedChunks / totalChunks) * 100);
                          toast.info(`Resumable upload detected! ${progress}% already uploaded. Click "Resume Upload" to continue.`);
                        }
                      }
                    }}
                  />
                  <div className="form-text">Upload zip file containing media assets (supports 2+ GB files with resumable upload)</div>
                  
                  {/* Resume Upload Alert */}
                  {resumableState && selectedFile && (
                    <div className="alert alert-info mt-3 d-flex align-items-center justify-content-between">
                      <div>
                        <strong>Resumable Upload Detected!</strong>
                        <div className="small mt-1">
                          File: {selectedFile.name} ({Math.round(selectedFile.size / 1024 / 1024)} MB)
                          <br />
                          Progress: {Math.round((resumableState.uploadedChunks.size / Math.ceil(selectedFile.size / (10 * 1024 * 1024))) * 100)}% uploaded
                        </div>
                      </div>
                      <Button 
                        variant="success" 
                        size="sm"
                        onClick={() => {
                          if (selectedFile) {
                            handleFileUpload(selectedFile, formik.values);
                          }
                        }}
                      >
                        <KTIcon iconName="arrow-right" className="me-1" />
                        Resume Upload
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <label className="form-label required">Thumbnails</label>
                  <Form.Control
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/svg+xml,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg"
                    multiple
                    onChange={handleThumbnailChange}
                    disabled={isUploading}
                  />
                  {thumbnails.length === 0 && (
                    <div className="form-text text-danger">At least one thumbnail is required</div>
                  )}
                  {thumbnails.length > 0 && (
                    <div className="mt-3">
                      <div className="row g-3">
                        {thumbnails.map((thumb, index) => (
                          <div key={index} className="col-md-3 col-sm-4 col-6">
                            <div className="position-relative border rounded p-2">
                              <img
                                src={thumb.preview}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-100 rounded"
                                style={{ height: '150px', objectFit: 'cover' }}
                              />
                              {thumb.isPrimary && (
                                <div className="position-absolute top-0 start-0 m-2">
                                  <span className="badge badge-success">Primary</span>
                                </div>
                              )}
                              <div className="position-absolute top-0 end-0 m-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light"
                                  onClick={() => removeThumbnail(index)}
                                  disabled={isUploading}
                                >
                                  <KTIcon iconName="cross" className="fs-5" />
                                </button>
                              </div>
                              {!thumb.isPrimary && (
                                <div className="mt-2 text-center">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setPrimaryThumbnail(index)}
                                    disabled={isUploading}
                                  >
                                    Set as Primary
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <label className="form-label required">Tags</label>
                  <div className="position-relative" data-tags-dropdown>
                    {/* Multiselect Input */}
                    <div 
                      className="form-control form-control-solid d-flex align-items-center flex-wrap gap-2"
                      style={{ minHeight: '42px', cursor: 'pointer' }}
                      onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
                    >
                      {(selectedTagIds.length === 0 && newTags.length === 0) ? (
                        <span className="text-muted">Select tags or type to add new...</span>
                      ) : (
                        <>
                          {selectedTagIds.map(tagId => {
                            const tag = tags.find(t => t.id === tagId);
                            if (!tag) return null;
                            return (
                              <span 
                                key={tagId}
                                className="badge badge-light-primary d-inline-flex align-items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTag(tagId);
                                }}
                              >
                                {tag.tag_title}
                                <KTIcon 
                                  iconName="cross" 
                                  className="fs-6 cursor-pointer"
                                />
                              </span>
                            );
                          })}
                          {newTags.map((newTag, idx) => (
                            <span 
                              key={`new-${idx}`}
                              className="badge badge-light-success d-inline-flex align-items-center gap-1"
                            >
                              {newTag} (New)
                            </span>
                          ))}
                        </>
                      )}
                      <div className="ms-auto d-flex align-items-center gap-2">
                        {(selectedTagIds.length > 0 || newTags.length > 0) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-icon btn-light"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTagIds([]);
                              setNewTags([]);
                            }}
                            title="Clear all tags"
                          >
                            <KTIcon iconName="cross" className="fs-5" />
                          </button>
                        )}
                        <KTIcon 
                          iconName={isTagsDropdownOpen ? 'up' : 'down'} 
                          className="fs-5 text-muted"
                        />
                      </div>
                    </div>

                    {/* Dropdown Menu */}
                    {isTagsDropdownOpen && (
                      <div 
                        className="position-absolute w-100 bg-white border border-gray-300 rounded shadow-lg"
                        style={{ 
                          top: '100%', 
                          zIndex: 1000, 
                          maxHeight: '300px', 
                          overflowY: 'auto',
                          marginTop: '4px'
                        }}
                      >
                        {/* Search/Add Input */}
                        <div className="p-3 border-bottom">
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search tags or type to add new..."
                              value={tagSearchQuery}
                              onChange={(e) => setTagSearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddNewTag();
                                }
                              }}
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddNewTag();
                              }}
                            >
                              Add New
                            </Button>
                          </div>
                        </div>

                        {/* Tags List */}
                        <div className="p-2">
                          {isLoadingTags ? (
                            <div className="text-center py-3">
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              <span className="text-muted fs-7">Loading tags...</span>
                            </div>
                          ) : filteredTagsForDropdown.length === 0 ? (
                            <div className="text-center py-3 text-muted fs-7">
                              {tagSearchQuery ? 'No tags found. Press "Add New" to create.' : 'No tags available'}
                            </div>
                          ) : (
                            filteredTagsForDropdown.map(tag => {
                              const isSelected = selectedTagIds.includes(tag.id);
                              return (
                                <div
                                  key={tag.id}
                                  className={`p-2 rounded cursor-pointer ${isSelected ? 'bg-light-primary' : 'hover:bg-light'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTagToggle(tag.id);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className={isSelected ? 'fw-bold' : ''}>{tag.tag_title}</span>
                                    {isSelected && (
                                      <KTIcon iconName="check" className="fs-5 text-primary" />
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-5">
                  <label className="form-label required">Source Platform</label>
                  <Form.Select
                    name="platform_id"
                    value={formik.values.platform_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.platform_id && formik.touched.platform_id}
                    disabled={isLoadingPlatforms}
                  >
                    <option value="">Select Platform</option>
                    {platforms.map(platform => (
                      <option key={platform.id} value={String(platform.id)}>
                        {platform.platform_name}
                      </option>
                    ))}
                  </Form.Select>
                  {isLoadingPlatforms && <div className="form-text">Loading platforms...</div>}
                  {formik.errors.platform_id && formik.touched.platform_id && (
                    <div className="text-danger">{formik.errors.platform_id}</div>
                  )}
                </div>

                <div className="mb-5">
                  <label className="form-label required">Source URL</label>
                  <Form.Control
                    type="url"
                    name="source_url"
                    value={formik.values.source_url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.source_url && formik.touched.source_url}
                    placeholder="https://..."
                  />
                  {formik.errors.source_url && formik.touched.source_url && (
                    <div className="text-danger">{formik.errors.source_url}</div>
                  )}
                </div>

                <div className="mb-5">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <label className="form-label required">Licenses</label>
                    <Button variant="outline-primary" size="sm" onClick={handleLicenseAdd} disabled={isUploading}>
                      <KTIcon iconName="plus" /> Add License
                    </Button>
                  </div>
                  {licenses.length === 0 && (
                    <div className="form-text text-danger mb-3">At least one license is required</div>
                  )}
                  {licenses.map((license, index) => {
                    const isExisting = license.isExisting === true;
                    const subprojectData = license.subprojectData;
                    // Get project name from subprojectData.project.project_name or find in projects list
                    const projectId = subprojectData?.project_id || subprojectData?.project?.id;
                    const projectName = subprojectData?.project?.project_name || (projectId ? projects.find(p => p.id === Number(projectId))?.project_name : '');
                    const subprojectName = subprojectData?.subproject_name || subprojectData?.title || 'N/A';
                    
                    return (
                      <div key={index} className="card mb-3">
                        <div className="card-body">
                          <div className="d-flex justify-content-between mb-3">
                            <h6>
                              License {index + 1}
                              {isExisting && <Badge bg="secondary" className="ms-2">Existing</Badge>}
                            </h6>
                            {!isExisting && (
                              <Button variant="link" size="sm" onClick={() => removeLicense(index)} disabled={isUploading}>
                                <KTIcon iconName="trash" />
                              </Button>
                            )}
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label required">Project</label>
                              {isExisting && subprojectData ? (
                                <Form.Control
                                  type="text"
                                  value={projectName || 'N/A'}
                                  disabled
                                  className="bg-light"
                                />
                              ) : (
                                <>
                                  <Form.Select
                                    value={license.project_id ? String(license.project_id) : ''}
                                    onChange={async (e) => {
                                      const projectId = e.target.value;
                                      const updated = [...licenses];
                                      updated[index] = { 
                                        ...updated[index], 
                                        project_id: projectId,
                                        subproject_id: '' // Reset subproject when project changes
                                      };
                                      setLicenses(updated);
                                      
                                      // Load subprojects for selected project
                                      if (projectId) {
                                        await loadSubprojectsForProject(projectId);
                                      } else {
                                        setSubprojects([]);
                                      }
                                    }}
                                    disabled={isLoadingProjects || isUploading}
                                  >
                                    <option value="">Select Project</option>
                                    {projects.map(project => (
                                      <option key={project.id} value={String(project.id)}>
                                        {project.project_name}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {isLoadingProjects && <div className="form-text">Loading projects...</div>}
                                  {!license.project_id && (
                                    <div className="form-text text-danger">Project is required</div>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label required">Subproject</label>
                              {isExisting && subprojectData ? (
                                <Form.Control
                                  type="text"
                                  value={`${subprojectName} (Project: ${projectName || 'N/A'})`}
                                  disabled
                                  className="bg-light"
                                />
                              ) : (
                                <>
                                  <Form.Select
                                    value={license.subproject_id ? String(license.subproject_id) : ''}
                                    onChange={(e) => {
                                      const updated = [...licenses];
                                      updated[index] = { 
                                        ...updated[index], 
                                        subproject_id: e.target.value
                                      };
                                      setLicenses(updated);
                                    }}
                                    disabled={!license.project_id || isLoadingProjects || isLoadingSubprojects || isUploading}
                                  >
                                    <option value="">Select Subproject</option>
                                    {isLoadingSubprojects ? (
                                      <option value="" disabled>Loading subprojects...</option>
                                    ) : license.project_id && subprojects
                                      .filter(sp => String(sp.project_id) === String(license.project_id))
                                      .map(subproject => (
                                        <option key={subproject.id} value={String(subproject.id)}>
                                          {subproject.title || subproject.subproject_name || 'Untitled'}
                                        </option>
                                      ))}
                                  </Form.Select>
                                  {!license.project_id && (
                                    <div className="form-text text-muted">Please select a project first</div>
                                  )}
                                  {license.project_id && !license.subproject_id && (
                                    <div className="form-text text-danger">Subproject is required</div>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label required">License File</label>
                              <Form.Control
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={(e) => handleLicenseFileChange(index, e as any)}
                                disabled={isExisting || isUploading}
                                required={!isExisting}
                                className={!isExisting && !license.license_file ? 'border-danger' : ''}
                              />
                              {isExisting && license.license_url && (
                                <div className="form-text text-muted mt-1">
                                  Existing file: {license.license_url}
                                </div>
                              )}
                              {!isExisting && !license.license_file && (
                                <div className="form-text text-danger mt-1">License File is required</div>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label required">Purchased Date</label>
                              <Form.Control
                                type="date"
                                value={license.purchased_date ? (license.purchased_date.includes('T') ? license.purchased_date.split('T')[0] : license.purchased_date) : ''}
                                onChange={(e) => {
                                  // Date input always returns YYYY-MM-DD format
                                  updateLicense(index, 'purchased_date', e.target.value);
                                }}
                                disabled={isExisting || isUploading}
                                required={!isExisting}
                                className={isExisting ? 'bg-light' : (!license.purchased_date ? 'border-danger' : '')}
                              />
                              {!isExisting && !license.purchased_date && (
                                <div className="form-text text-danger mt-1">Purchased Date is required</div>
                              )}
                            </div>
                            <div className="col-12">
                              <label className="form-label">License Text / Description</label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="License Text"
                                value={license.license_text || ''}
                                onChange={(e) => updateLicense(index, 'license_text', e.target.value)}
                                disabled={isExisting || isUploading}
                                className={isExisting ? 'bg-light' : ''}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {uploadProgress && (
                  <div className="mb-5">
                    <div className="d-flex justify-content-between mb-2">
                      <span>{uploadProgress.file_name}</span>
                      <span>{uploadProgress.percentage}%</span>
                    </div>
                    <ProgressBar
                      now={uploadProgress.percentage}
                      label={`${uploadProgress.percentage}%`}
                      variant={
                        uploadProgress.status === 'completed' ? 'success' : 
                        uploadProgress.status === 'resuming' ? 'info' : 
                        uploadProgress.status === 'failed' ? 'danger' : 
                        'primary'
                      }
                    />
                    <div className="text-muted mt-1">
                      {uploadProgress.status === 'resuming' && <span className="text-info">🔄 Resuming... </span>}
                      {formatBytes(uploadProgress.uploaded_bytes)} / {formatBytes(uploadProgress.total_bytes)}
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="alert alert-warning mb-4">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="information-5" className="fs-2x text-warning me-3" />
                      <div className="flex-grow-1">
                        <strong>Upload in Progress!</strong>
                        <div className="mt-1">
                          Please do not navigate away or close this page. If you go back now, your file upload will not complete.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-end gap-2">
                  <Button 
                    variant="secondary" 
                    onClick={() => {
                      if (isUploading) {
                        if (window.confirm('Upload is in progress. Are you sure you want to leave? Your file upload will not complete.')) {
                          navigate('/digital-assets');
                        }
                      } else {
                        navigate('/digital-assets');
                      }
                    }}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Cancel'}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isUploading || !selectedFile}>
                    {isUploading 
                      ? (uploadProgress?.status === 'resuming' ? 'Resuming...' : 'Uploading...') 
                      : (resumableState ? 'Resume Upload' : (isEditMode ? 'Update & Upload Asset' : 'Upload Asset'))
                    }
                  </Button>
                </div>
              </Form>
              )}
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    </>
  );
};

export { UploadAssetPage };

